import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Grid, Typography, TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { withStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { useLocation } from 'react-router';

const autocompleteService = { current: null };
const LOCATION_FIELDS = ['student_location', 'preceptor_location'];

const CustomTextField = withStyles(() => ({
  root: {
    '& .MuiFilledInput-root': {
      paddingLeft: 7,
      paddingTop: 15,
      height: 45,

      '&:hover': {
        background: '#fff',
      },
    },
    '& .MuiFormHelperText-root': {
      color: '#4E6363',
    },
  },
}))(TextField);

const GoogleMapsAutocomplete = ({
  name, label, index, value, onChange, setDisabledRadius, required, error, helperText,
}) => {
  const location = useLocation();
  const pathNames = location.pathname.split('/').filter(e => e);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const handleChange = (event, newValue) => {
    setOptions(newValue ? [newValue, ...options] : options);
    if (setDisabledRadius) {
      setDisabledRadius(disabledRadius => disabledRadius.map((v, i) => (
        i !== index ? v : Boolean(newValue && newValue.types?.includes('administrative_area_level_1')))));
    }
    if (pathNames[0] === 'profile' && !LOCATION_FIELDS.includes(name)) {
      onChange(newValue && newValue.description);
    } else {
      onChange(newValue);
    }
  };

  const fetch = useMemo(
    () => throttle((request, callback) => {
      autocompleteService.current.getPlacePredictions(request, callback);
    }, 200),
    [],
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    } if (!autocompleteService.current) {
      return undefined;
    } if (inputValue === '') {
      if (value?.description) {
        fetch({ input: value.description || value }, results => {
          if (active) {
            const newOptions = results ? [...results] : [];
            setOptions(newOptions);

            if (newOptions.length > 0) {
              handleChange(null, newOptions[0]);
            }
          }
        });
      }
      return undefined;
    }

    fetch({ input: inputValue }, results => {
      if (active) {
        let newOptions = [];
        if (value) {
          newOptions = [value];
        } if (results) {
          newOptions = [...newOptions, ...results];
        }
        setOptions(newOptions);
      }
    });

    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inputValue, fetch, setOptions]);

  return (
    <>
      <Autocomplete
        id="location-autocomplete"
        getOptionLabel={option => (typeof option === 'string' ? option : option.description)}
        getOptionSelected={(option, val) => (option.place_id === val.place_id)}
        filterOptions={x => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        fullWidth
        value={value}
        onChange={handleChange}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        name={name}
        renderInput={params => (
          <CustomTextField
            {...params}
            fullWidth
            placeholder="start typing for options"
            required={required}
            label={label}
            error={error}
            helperText={helperText}
            variant="filled"
            size="medium"
          />
        )}
        renderOption={option => {
          const matches = option.structured_formatting?.main_text_matched_substrings || [];
          const parts = parse(
            option.structured_formatting?.main_text || '',
            matches.map(match => [match.offset, match.offset + match.length]),
          );
          return (
            <Grid container alignItems="center">
              <Grid item xs>
                {parts.map((p, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={i} style={{ fontWeight: p.highlight ? 700 : 400 }}>
                    {p.text}
                  </span>
                ))}
                <Typography variant="subtitle2" color="textSecondary">
                  {option.structured_formatting?.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          );
        }}
      />
      {/* {(helperText) && (
        <FormHelperText style={{ marginLeft: 13 }}>{helperText}</FormHelperText>
      )} */}
    </>
  );
};

GoogleMapsAutocomplete.defaultProps = {
  name: 'location',
  label: 'Location',
  index: 0,
  value: null,
  setDisabledRadius: null,
  required: false,
  error: false,
  helperText: '',
};

GoogleMapsAutocomplete.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  index: PropTypes.number,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      description: PropTypes.string,
      place_id: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  setDisabledRadius: PropTypes.func,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
};

export default GoogleMapsAutocomplete;
